import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "searchInput",
    "searchResults",
    "parentForm",
    "existingParentInfo",
    "submitButton",
    "createNewBtn",
    "useExistingBtn",
  ];

  static values = {
    searchUrl: String,
    studentId: String,
    noResultsText: String,
    noResultsHelpText: String,
    selectButtonText: String,
    alreadyAssociatedText: String,
    foundParentsTitle: String,
    selectedParentTitle: String,
    createNewInsteadText: String,
    associateExistingButtonText: String,
    createAndAssociateButtonText: String,
  };

  connect() {
    this.timeout = null;
    this.selectedParentId = null;
    console.log("Parent search controller connected");
  }

  disconnect() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  search() {
    const query = this.searchInputTarget.value.trim();

    // Clear previous timeout
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    // Debounce the search
    this.timeout = setTimeout(() => {
      this.performSearch(query);
    }, 300);
  }

  async performSearch(query) {
    if (query.length < 2) {
      this.hideSearchResults();
      return;
    }

    try {
      const response = await fetch(
        `${this.searchUrlValue}?search_term=${encodeURIComponent(query)}`,
      );
      const data = await response.json();

      this.displaySearchResults(data.parents);
    } catch (error) {
      console.error("Search error:", error);
      this.hideSearchResults();
    }
  }

  displaySearchResults(parents) {
    if (parents.length === 0) {
      this.searchResultsTarget.innerHTML = `
        <div class="p-4 text-center text-gray-500">
          <svg class="w-8 h-8 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.22 0-4.239-.732-5.937-1.972"></path>
          </svg>
          <p>${this.noResultsTextValue}</p>
          <p class="text-sm">${this.noResultsHelpTextValue}</p>
        </div>
      `;
      this.searchResultsTarget.classList.remove("hidden");
      return;
    }

    const resultsHtml = parents
      .map((parent) => {
        const canAssociate = parent.can_associate;
        const buttonClass = canAssociate
          ? "ds-btn ds-btn--primary ds-btn--sm"
          : "ds-btn ds-btn--outline ds-btn--sm is-disabled";

        return `
        <div class="flex items-center justify-between p-3 border-b border-gray-200 last:border-b-0">
          <div class="flex-1">
            <h4 class="font-medium text-gray-900">${parent.full_name}</h4>
            <div class="text-sm text-gray-500">
              <p>📧 ${parent.email}</p>
              <p>📞 ${parent.phone}</p>
              ${parent.matricule ? `<p>🆔 ${parent.matricule}</p>` : ""}
            </div>
          </div>
          <button
            type="button"
            class="${buttonClass}"
            data-action="click->parent-search#selectParent"
            data-parent-id="${parent.id}"
            data-parent-name="${parent.full_name}"
            data-parent-email="${parent.email}"
            data-parent-phone="${parent.phone}"
            ${!canAssociate ? "disabled" : ""}
          >
            ${
              canAssociate
                ? this.selectButtonTextValue
                : this.alreadyAssociatedTextValue
            }
          </button>
        </div>
      `;
      })
      .join("");

    this.searchResultsTarget.innerHTML = `
      <div class="bg-white border border-gray-200 rounded-md shadow-sm">
        <div class="p-3 bg-gray-50 border-b border-gray-200">
          <h3 class="text-sm font-medium text-gray-900">${this.foundParentsTitleValue}</h3>
        </div>
        ${resultsHtml}
      </div>
    `;

    this.searchResultsTarget.classList.remove("hidden");
  }

  selectParent(event) {
    const button = event.currentTarget;
    const parentId = button.dataset.parentId;
    const parentName = button.dataset.parentName;
    const parentEmail = button.dataset.parentEmail;
    const parentPhone = button.dataset.parentPhone;

    this.selectedParentId = parentId;

    this.hideSearchResults();
    this.hideParentForm();
    this.toggleParentFormInputs(true);

    this.showExistingParentInfo(parentName, parentEmail, parentPhone);

    this.updateSubmitButton(true);
  }

  showExistingParentInfo(name, email, phone) {
    this.existingParentInfoTarget.innerHTML = `
      <div class="bg-cta-shared border border-cta rounded-sm p-4 mb-6">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-cta" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
            </svg>
          </div>
          <div class="ml-3">
            <h4 class="text-sm font-medium text-cta-darker">${this.selectedParentTitleValue}</h4>
            <div class="mt-1 text-sm text-cta-darker">
              <p><strong>${name}</strong></p>
              <p>📧 ${email}</p>
              <p>📞 ${phone}</p>
            </div>
            <div class="mt-3">
              <button
                type="button"
                class="ds-link text-sm underline"
                data-action="click->parent-search#createNew"
              >
                ${this.createNewInsteadTextValue}
              </button>
            </div>
          </div>
        </div>
      </div>
      <input type="hidden" name="parent[existing_parent_id]" value="${this.selectedParentId}">
    `;
    this.existingParentInfoTarget.classList.remove("hidden");
  }

  createNew() {
    this.selectedParentId = null;
    this.hideExistingParentInfo();
    this.existingParentInfoTarget.innerHTML = "";
    this.showParentForm();
    this.updateSubmitButton(false);
    this.searchInputTarget.value = "";
    this.hideSearchResults();
    this.toggleParentFormInputs(false);
  }

  hideSearchResults() {
    this.searchResultsTarget.classList.add("hidden");
  }

  hideParentForm() {
    this.parentFormTarget.classList.add("hidden");
  }

  showParentForm() {
    this.parentFormTarget.classList.remove("hidden");
  }

  hideExistingParentInfo() {
    this.existingParentInfoTarget.classList.add("hidden");
  }

  toggleParentFormInputs(disabled) {
    const inputs = this.parentFormTarget.querySelectorAll("input, select");
    inputs.forEach((input) => {
      input.disabled = disabled;
    });
  }

  updateSubmitButton(isExisting) {
    // L'habillage du bouton reste piloté par le Design System (ds-btn ds-btn--primary).
    // On ne change que le libellé selon le mode (création vs association).
    const label = isExisting
      ? this.associateExistingButtonTextValue
      : this.createAndAssociateButtonTextValue;
    const btn = this.submitButtonTarget;
    // f.submit rend un <input type="submit"> (libellé = value) ;
    // un éventuel <button> utiliserait textContent.
    if (btn.tagName === "INPUT") {
      btn.value = label;
    } else {
      btn.textContent = label;
    }
  }
}
